<script setup lang="ts">
import { computed } from 'vue'

export interface BadgeProps {
  /** Not used if the `variant` prop is set to `hint`. */
  emphasis?: 'low' | 'medium' | 'high'
  /** Hide the Badge if the value is zero. */
  hideZero?: boolean
  /** The number `value` should be capped at. */
  max?: number
  size?: 'sm' | 'md'
  tone?: 'neutral' | 'info' | 'critical'
  /** The value to display.  Not used if `variant` prop is set to `hint`. */
  value?: number
  variant?: 'count' | 'hint'
}

const props = withDefaults(defineProps<BadgeProps>(), {
  emphasis: 'high',
  hideZero: false,
  max: 99,
  size: 'md',
  tone: 'neutral',
  value: undefined,
  variant: 'count',
})

const emphasisString = computed<BadgeProps['emphasis']>(() =>
  props.variant === 'hint' ? 'high' : props.emphasis,
)

const displayValue = computed(() => {
  if (Number.isNaN(props.value * 1) || Number.isNaN(props.max)) return '0'
  if (props.value === Infinity) return '∞'
  if (props.max < props.value) return `${props.max}+`

  return `${props.value}`
})
</script>

<template>
  <div
    class="badge"
    :class="[
      emphasisString,
      size,
      `tone-${tone}`,
      variant,
      { hidden: hideZero && displayValue === '0' },
    ]"
  >
    <div v-if="variant !== 'hint'" class="content">
      {{ displayValue }}
    </div>
  </div>
</template>

<style scoped>
.badge {
  display: inline-flex;
  align-items: center;
  background-color: var(--badge-background);
  border-radius: 9999px;
  box-shadow: inset 0 0 0 1px var(--badge-border, transparent);
  user-select: none;
}

.hidden {
  display: none;
}

.tone-neutral {
  --badge-color-strong: var(--color-grey--dark);
  --badge-color-default: var(--color-grey);
  --badge-color-weak: var(--color-grey--lightest);
}

.tone-info {
  --badge-color-strong: var(--color-blue--dark);
  --badge-color-default: var(--color-blue);
  --badge-color-weak: var(--color-blue--lighter);
}

.tone-critical {
  --badge-color-strong: var(--color-red--dark);
  --badge-color-default: var(--color-red);
  --badge-color-weak: var(--color-red--lighter);
}

.sm {
  --badge-size: 16px;
  --badge-hint-size: 8px;
  --badge-padding: 4px;
  --badge-font-size: 0.625rem;
}

.md {
  --badge-size: 20px;
  --badge-hint-size: 10px;
  --badge-padding: 6px;
  --badge-font-size: 0.75rem;
}

.low {
  --badge-text: var(--badge-color-default);
  --badge-background: var(--color-white);
  --badge-border: rgb(0 0 0 / 12%);
}

.medium {
  --badge-text: var(--badge-color-strong);
  --badge-background: var(--badge-color-weak);
}

.high {
  --badge-text: var(--color-white);
  --badge-background: var(--badge-color-default);
}

.count {
  min-inline-size: var(--badge-size);
  min-block-size: var(--badge-size);
  padding: 0 var(--badge-padding);
}

.hint {
  inline-size: var(--badge-hint-size);
  block-size: var(--badge-hint-size);
}

.content {
  font-family: var(--font-regular);
  font-size: var(--badge-font-size);
  font-weight: 600;
  line-height: 1;
  color: var(--badge-text);
  text-box: trim-both cap alphabetic;
}
</style>
